<template>
  <div class="overlay">
    <div class="picture-modal">
      <button class="close-btn" @click="$emit('close')">
        <b-icon-x/>
      </button>
      <div class="content-container">
        <!-- Left Section: Image preview (with banderol overlay) and actions -->
        <div class="left-section">
          <div class="preview-container">
            <div class="crop-preview" v-if="image">
              <Cropper
                  ref="cropper"
                  class="circle-cropper"
                  :src="image"
                  :stencil-size="{ width: 280, height: 243 }"
                  :aspect-ratio="1.153"
                  :resize-image="{ adjustStencil: false }"
                  :transform-image="{ adjustStencil: false }"
                  stencil-component="circle-stencil"
                  :stencil-props="{
                    handlers: {},
                    movable: false,
                    resizable: false,
                    previewClass: 'circle-cropper__preview',
                  }"
                  :canvas="{
                    height: 720,
                    width: 830,
                  }"
                  :auto-zoom="false"
                  :background="false"
                  :movable="true"
                  :wheel-resize="false"
                  :mouse-move="true"
                  :touch-move="true"
                  image-restriction="stencil"
                  :minCropBoxWidth="246"
                  :minCropBoxHeight="214"
                  :minContainerHeight="214"
                  :minCanvasHeight="214"
              />
            </div>
            <div class="banderol-container">
              <img
                  :src="image && image.indexOf('new-dog') === -1
                  ? 'https://media.futalis.com/futalis/fa/photo-upload-banderol_white.png'
                  : 'https://media.futalis.com/futalis/fa/photo-upload-banderol-avatar.png'"
                  alt="banderol"
                  class="banderol-image"
              />
              <p class="pet-name" v-html="pet.petName || pet.name || 'Hundename'"></p>
            </div>
          </div>
          <div class="actions-row"
               :class="{ 'disabled-actions': image === null || image.indexOf('new-dog') !== -1 }">
            <button class="action-button" @click.prevent="rotate(-90)" title="Drehen nach links">
              <i class="fas fa-undo"></i>
            </button>
            <button class="action-button" @click.prevent="rotate(90)" title="Drehen nach rechts">
              <i class="fas fa-redo"></i>
            </button>
            <button class="action-button" @click.prevent="flipHorizontal" title="Horizontal spiegeln">
              <i class="fas fa-arrows-alt-h"></i>
            </button>
            <button class="action-button" @click.prevent="zoomIn" title="Einzoomen">
              <i class="fas fa-search-plus"></i>
            </button>
            <button class="action-button" @click.prevent="zoomOut" title="Auszoomen">
              <i class="fas fa-search-minus"></i>
            </button>
            <button class="action-button" @click.prevent="resetCropper" title="Zurücksetzen">
              <i class="fas fa-sync-alt"></i>
            </button>
            <button class="action-button text-danger" @click.prevent="deleteImage" title="Foto löschen">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
        <!-- Right Section -->
        <div class="right-section">
          <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="file-input"
              @change="handleFileChange"
          />
          <div class="info-container">
            <h3 class="headline">
              Gestalten Sie die Futtertüte mit einem Hundefoto
            </h3>
            <p class="description">
              Hinweis: Für optimale Druckqualität empfehlen wir ein Foto in guter Auflösung. Unterstützte Formate: JPG, PNG, GIF.
            </p>
          </div>
          <button v-if="image" class="futalis-btn futalis-button-green" @click="cropImage">
            Foto hochladen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Cropper } from "vue-advanced-cropper";

export default {
  name: "PictureUpload",
  props: {
    close: { type: Function, required: false },
    pet: { type: Object, required: false },
  },
  components: { Cropper },
  data() {
    return {
      image: null,
      currentZoom: 1,
    };
  },
  methods: {
    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.image = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    cropImage() {
      const cropper = this.$refs.cropper;
      if (!cropper) {
        console.error("Cropper instance not found.");
        return;
      }
      const result = cropper.getResult();
      if (!result || !result.canvas) {
        console.error("Failed to generate cropped canvas. Result:", result);
        return;
      }
      result.canvas.toBlob((blob) => {
        if (!blob) {
          console.error("toBlob failed.");
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result;
          const petId = this.pet.id ?? this.pet?.petId;
          this.$emit("selectPicture", { image: base64Data, petId });
          document.body.style.overflow = "";
        };
        reader.readAsDataURL(blob);
      });
    },
    rotate(angle) {
      const cropper = this.$refs.cropper;
      if (cropper) cropper.rotate(angle);
    },
    flipHorizontal() {
      const cropper = this.$refs.cropper;
      if (cropper) cropper.flip(true, false);
    },
    resetCropper() {
      const cropper = this.$refs.cropper;
      if (cropper) cropper.reset();
    },
    zoomIn() {
      const cropper = this.$refs.cropper;
      if (!cropper) return;
      this.currentZoom = Math.min(3, this.currentZoom + 0.1);
      cropper.zoom(this.currentZoom);
    },
    zoomOut() {
      const cropper = this.$refs.cropper;
      if (!cropper) return;
      this.currentZoom = Math.max(0.5, this.currentZoom - 0.1);
      cropper.zoom(this.currentZoom);
    },
    deleteImage() {
      this.image = null;
      this.$emit("deleteImage");
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
  },
  mounted() {
    this.triggerFileInput();
    if (this.pet?.image?.value) {
      this.image = this.pet.image.value;
    }
  },
  model: {
    prop: 'image',
    event: 'selectPicture'
  }
};
</script>

<style scoped>
.picture-modal {
  max-width: 900px;
  width: 90%;
  background-color: #ffffff;
  position: fixed;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
}

.close-btn {
  position: absolute;
  font-size: 28px;
  top: 0;
  right: 10px;
  border: none;
  background: none;
  cursor: pointer;
}

.content-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
}

.left-section {
  flex: 1 1 60%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.preview-container {
  width: 360px;
  height: 260px;
  margin-bottom: 20px;
  position: relative;
}

.crop-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banderol-container {
  pointer-events: none;
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  width: 100%;
  text-align: center;
}

.banderol-image {
  width: 100%;
  height: auto;
}

.banderol-container .pet-name {
  position: absolute;
  top: 75%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 26px;
  font-family: "Amatic SC", cursive;
  color: #333;
  font-weight: bold;
  margin: 0;
  width: 80%;
}

.actions-row {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 70%;
  margin-top: 10px;
  margin-bottom: 20px;
  gap: 15px;
  flex-wrap: wrap;
}

.actions-row .action-button {
  background-color: #f0f0f0;
  border: none;
  border-radius: 5px;
  padding: 10px;
  cursor: pointer;
  font-size: 18px;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
}

.disabled-actions {
  pointer-events: none;
  opacity: 0.5;
  filter: grayscale(80%);
}

.actions-row .action-button:hover {
  background-color: #f2f2f2;
}

.actions-row .action-button i {
  color: #4a4a49;
}

.actions-row .action-button.text-danger i {
  color: #d40000;
}

.right-section {
  flex: 1 1 50%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 20px;
  gap: 20px;
}

.file-input {
  display: block;
  width: 100%;
  font-size: 14px;
  padding: 8px;
}

.info-container {
  text-align: left;
}

.headline {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.description {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
}

.futalis-btn {
  padding: 12px 20px;
  font-size: 16px;
  border: none;
  border-radius: 8px;
  color: white;
  width: 100%;
  text-align: center;
}

.futalis-button-green {
  background-color: #bccf00;
  transition: background-color 0.3s ease;
}

.futalis-button-green:hover {
  background-color: #93ab00;
}

@media (max-width: 768px) {
  .content-container {
    flex-direction: column;
    gap: 10px;
  }
  .left-section,
  .right-section {
    width: 100%;
    flex: 1 1 100%;
  }
  .preview-container {
    width: 100%;
    height: auto;
    overflow: hidden;
  }
}

@media only screen and (-webkit-min-device-pixel-ratio: 2),
  only screen and (min--moz-device-pixel-ratio: 2),
  only screen and (-o-min-device-pixel-ratio: 2/1),
  only screen and (min-device-pixel-ratio: 2),
  only screen and (min-resolution: 192dpi),
  only screen and (min-resolution: 2dppx) {
}
</style>
<style>
.vue-advanced-cropper__stencil {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  border-radius: 0 !important;
}

.vue-advanced-cropper {
  cursor: grab;
}

.vue-advanced-cropper:active {
  cursor: grabbing;
}

.vue-advanced-cropper__handler {
  display: none !important;
}

.vue-circle-stencil{
  pointer-events: none !important;
}
</style>